import { ComponentType, lazy } from "react";

const RELOAD_KEY = "cws:chunk-reload";

export function isChunkLoadError(err: unknown): boolean {
  const msg = (err as { message?: string } | null)?.message || String(err || "");
  const name = (err as { name?: string } | null)?.name || "";
  return (
    name === "ChunkLoadError" ||
    /Importing a module script failed/i.test(msg) ||
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /error loading dynamically imported module/i.test(msg) ||
    /Loading chunk [\d]+ failed/i.test(msg) ||
    /Loading CSS chunk/i.test(msg)
  );
}

/** Reload once to pick up a new index.html after a deploy; guard against loops. */
export function reloadForNewVersion(): void {
  try {
    if (sessionStorage.getItem(RELOAD_KEY)) return;
    sessionStorage.setItem(RELOAD_KEY, "1");
  } catch {
    /* ignore */
  }
  window.location.reload();
}

/** Clear the one-shot reload flag — call after a successful chunk load. */
function clearReloadFlag(): void {
  try {
    sessionStorage.removeItem(RELOAD_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * React.lazy wrapper that retries a failed dynamic import once, then forces a
 * one-time full reload so the browser fetches the new index.html (and the new
 * hashed chunk file names) after a deploy.
 */
export function lazyWithRetry<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>,
) {
  return lazy(async () => {
    try {
      const mod = await factory();
      clearReloadFlag();
      return mod;
    } catch (err) {
      if (!isChunkLoadError(err)) throw err;
      // brief delay, then retry once
      await new Promise((r) => setTimeout(r, 400));
      try {
        const mod = await factory();
        clearReloadFlag();
        return mod;
      } catch (err2) {
        if (isChunkLoadError(err2)) {
          reloadForNewVersion();
          // Return a placeholder while the reload happens so Suspense doesn't throw.
          return { default: (() => null) as unknown as T };
        }
        throw err2;
      }
    }
  });
}
